// Substep 5-η.6: TopBar chrome — right side carries Clio connection
// status + a user menu dropdown that owns Profile + Sign Out. Sidebar
// footer is now identity-free; identity lives up here where every SaaS
// app puts it. Left side is intentionally empty for now (page title /
// breadcrumbs are a nice-to-have follow-up).

import React, { useState, useEffect, useRef } from "react";
import { C, ROLE_BADGE_COLORS, ROLE_LABELS } from "../constants.js";
import { NotificationBell } from "./NotificationBell.jsx";

// Height must match SIDEBAR_TOP_OFFSET in app.jsx and any main-content
// top padding so the TopBar doesn't overlap page content.
export const TOP_BAR_HEIGHT = 48;

export function TopBar({ authStatus, sidebarWidth, setPage, onNavigateToMatter,
                        isMobile = false, onOpenNav }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close the dropdown when clicking outside.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const authenticated = !!authStatus?.authenticated;
  const userName = authStatus?.user?.name || "";
  const userEmail = authStatus?.user?.email || "";
  const role = authStatus?.firm_user?.role || null;
  const roleColors = role ? (ROLE_BADGE_COLORS[role] || ROLE_BADGE_COLORS.pending) : null;
  const roleLabel = role ? (ROLE_LABELS[role] || role) : "";
  const initials = (userName || userEmail || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w.charAt(0).toUpperCase())
    .join("") || "?";

  return React.createElement("div", {
    style: {
      position: "fixed", top: 0, right: 0,
      // Full width on a phone: the sidebar is a drawer there, so there
      // is nothing to sit beside. Offsetting by 240px left the bar
      // 135px wide on a 375px screen.
      left: isMobile ? 0 : `${sidebarWidth}px`,
      background: "#fff",
      borderBottom: `1px solid ${C.warmGray}`,
      display: "flex", alignItems: "center",
      padding: isMobile ? "0 12px" : "0 20px",
      gap: "12px",
      // The notch inset is added AFTER the padding shorthand — set
      // before it, the shorthand silently resets it to zero and the bar
      // renders under the status bar on an installed PWA.
      paddingTop: isMobile ? "max(env(safe-area-inset-top), 0px)" : 0,
      // Height grows by the inset for the same reason, so the bar's
      // contents stay vertically centred below the notch.
      height: isMobile
        ? `calc(${TOP_BAR_HEIGHT}px + env(safe-area-inset-top))`
        : `${TOP_BAR_HEIGHT}px`,
      zIndex: 50,
      transition: "left 0.18s ease",
    },
  },
    // Only rendering on mobile: on desktop the nav is always visible
    // and a button to reveal it would be a control that does nothing.
    isMobile && React.createElement("button", {
      onClick: onOpenNav,
      "aria-label": "Open navigation",
      style: {
        border: "none", background: "transparent", cursor: "pointer",
        padding: "10px 12px", marginLeft: "2px", lineHeight: 0,
        // 44px is the minimum comfortable touch target; a 24px icon
        // with 10px of padding gets there.
        minWidth: "44px", minHeight: "44px",
      },
    },
      React.createElement("svg", {
        width: 22, height: 22, viewBox: "0 0 24 24", fill: "none",
        stroke: "#1B2A4A", strokeWidth: 2, strokeLinecap: "round",
      },
        React.createElement("line", { x1: 3, y1: 6, x2: 21, y2: 6 }),
        React.createElement("line", { x1: 3, y1: 12, x2: 21, y2: 12 }),
        React.createElement("line", { x1: 3, y1: 18, x2: 21, y2: 18 }),
      ),
    ),
    // Left: empty for now (page title / breadcrumbs future work)
    React.createElement("div", { style: { flex: 1 } }),

    // Substep 5-η.6.e reorder: Clio status → Bell → User pill.
    // Rationale: Clio status is passive info (leftmost, deprioritized),
    // Bell is occasional-glance (middle), User pill is the anchored
    // identity (rightmost). Original order (Bell first) put the most
    // attention-grabbing element farthest from the user's mental "who
    // am I" corner.
    authenticated
      ? React.createElement("span", {
          style: {
            display: "flex", alignItems: "center", gap: "6px",
            fontSize: "11px", color: C.textMuted, padding: "0 4px",
          },
          title: "Connected to Clio",
        },
          React.createElement("div", {
            style: {
              width: 8, height: 8, borderRadius: "50%",
              background: "#4ADE80",
            }
          }),
          "Clio",
        )
      : React.createElement("span", {
          style: {
            display: "flex", alignItems: "center", gap: "6px",
            fontSize: "11px", color: C.error, fontWeight: 600, padding: "0 4px",
          },
          title: "Not signed in to Clio",
        },
          React.createElement("div", {
            style: {
              width: 8, height: 8, borderRadius: "50%",
              background: C.error,
            }
          }),
          "Disconnected",
        ),

    // NotificationBell — inline (5-η.6.b). Only render when
    // authenticated; polling makes no sense on the login page.
    authenticated && React.createElement(NotificationBell, {
      onNavigateToMatter,
    }),

    // User menu (only when authenticated)
    // Substep 5-η.6.e: roomier pill — Aaron's "squished" complaint.
    // Bigger avatar (32px vs 28px), more horizontal padding, name-only
    // (role moved out of the pill; still lives in the dropdown card).
    // Chevron gets a bit more breathing room.
    authenticated && React.createElement("div", {
      ref: menuRef,
      style: { position: "relative" }
    },
      React.createElement("button", {
        onClick: () => setMenuOpen(v => !v),
        "aria-label": "Open user menu",
        title: userName + (userEmail ? ` (${userEmail})` : "") + (roleLabel ? ` — ${roleLabel}` : ""),
        style: {
          display: "flex", alignItems: "center", gap: "10px",
          padding: "4px 12px 4px 4px", borderRadius: "999px",
          background: menuOpen ? C.warmWhite : "transparent",
          border: `1px solid ${menuOpen ? C.warmGray : "transparent"}`,
          cursor: "pointer", transition: "all 0.15s",
          height: "36px",
        },
        onMouseEnter: e => { if (!menuOpen) e.currentTarget.style.background = C.warmWhite; },
        onMouseLeave: e => { if (!menuOpen) e.currentTarget.style.background = "transparent"; },
      },
        // Avatar circle with initials
        React.createElement("div", {
          style: {
            width: 30, height: 30, borderRadius: "50%",
            background: C.navy, color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "12px", fontWeight: 700, letterSpacing: "0.02em",
            flexShrink: 0,
          }
        }, initials),
        // Name only — role moved to the dropdown card. Keeps the pill
        // compact and reduces the "squished" look Aaron flagged.
        React.createElement("span", {
          style: {
            fontSize: "13px", color: C.textDark, fontWeight: 600,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            maxWidth: "180px", lineHeight: 1,
          }
        }, userName || userEmail || "User"),
        // Chevron
        React.createElement("svg", {
          width: 11, height: 11, viewBox: "0 0 24 24",
          fill: "none", stroke: C.textMuted, strokeWidth: 2.5, strokeLinecap: "round",
          style: {
            transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s", flexShrink: 0,
          }
        },
          React.createElement("polyline", { points: "6 9 12 15 18 9" }),
        ),
      ),

      // Dropdown menu
      menuOpen && React.createElement("div", {
        style: {
          position: "absolute", top: "calc(100% + 6px)", right: 0,
          minWidth: "220px",
          background: "#fff", border: `1px solid ${C.warmGray}`,
          borderRadius: "10px", padding: "6px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
          display: "flex", flexDirection: "column",
        }
      },
        // Identity block (larger name/email view)
        React.createElement("div", {
          style: {
            padding: "10px 12px", borderBottom: `1px solid ${C.warmGray}`,
            marginBottom: "4px",
          }
        },
          React.createElement("div", { style: { fontSize: "13px", color: C.navy, fontWeight: 600 } },
            userName || "—"),
          userEmail && React.createElement("div", {
            style: { fontSize: "11px", color: C.textMuted, marginTop: "2px",
                     wordBreak: "break-all" }
          }, userEmail),
          role && React.createElement("div", { style: { marginTop: "6px" } },
            React.createElement("span", {
              style: {
                display: "inline-block", padding: "2px 8px", borderRadius: "999px",
                background: roleColors?.bg || "#eee",
                color: roleColors?.fg || C.textMuted,
                fontSize: "10px", fontWeight: 700, letterSpacing: "0.05em",
                textTransform: "uppercase",
              }
            }, roleLabel),
          ),
        ),
        // My Profile
        React.createElement("button", {
          onClick: () => { setMenuOpen(false); setPage("my-profile"); },
          style: {
            padding: "8px 12px", textAlign: "left", border: "none",
            background: "transparent", color: C.textDark,
            fontSize: "13px", cursor: "pointer", borderRadius: "6px",
          },
          onMouseEnter: e => e.currentTarget.style.background = C.warmWhite,
          onMouseLeave: e => e.currentTarget.style.background = "transparent",
        }, "My Profile"),
        // Sign Out
        React.createElement("button", {
          onClick: () => { window.location.href = "/auth/logout"; },
          style: {
            padding: "8px 12px", textAlign: "left", border: "none",
            background: "transparent", color: C.textDark,
            fontSize: "13px", cursor: "pointer", borderRadius: "6px",
          },
          onMouseEnter: e => e.currentTarget.style.background = C.warmWhite,
          onMouseLeave: e => e.currentTarget.style.background = "transparent",
        }, "Sign Out"),
      ),
    ),
  );
}
